<template>
  <el-dialog
      :visible="visible"
      title="Planifier vos posts"
      :width="dialogWidth"
      @close="handleClose"
      :close-on-click-modal="false"
      class="publish-schedule-modal"
  >
    <p class="schedule-description">
      Choisissez quand publier votre contenu sur chacun des réseaux sociaux sélectionnés.
    </p>
    <div class="info-message" v-if="hasEmptyCaptions">
      <i class="el-icon-info"></i>
      <p>{{ missingMessage }}</p>
    </div>
    <div class="network-group" v-for="network in availableNetworks" :key="network.name">
      <div class="network-header">
        <div class="network-title-group">
          <div class="network-label">
            <img :src="network.icon" alt="" class="icon" />
            <span>{{ network.name.toUpperCase() }}</span>
            <el-tag type="success" v-if="isPublished(network.name)">{{ $t('general.calendar.postPublished') }}</el-tag>
          </div>
        </div>
      </div>

      <div class="network-controls">
        <el-date-picker
            v-if="!isPublished(network.name)"
            v-model="scheduleDates[network.name]"
            type="datetime"
            placeholder="JJ/MM/AAAA HH:mm"
            format="dd/MM/yyyy HH:mm"
            value-format="yyyy-MM-dd HH:mm:ss"
            :picker-options="pickerOptions"
            :disabled="publishNowOptions[network.name] || deleteOptions[network.name]"
            class="date-picker"
        />

        <div class="checkbox-group">
          <el-checkbox
              v-if="!isPublished(network.name)"
              v-model="publishNowOptions[network.name]"
              @change="onPublishNowChange(network.name, $event)"
              :disabled="(scheduleDates[network.name] && scheduleDates[network.name].trim() !== '')"
              class="publish-now-checkbox"
          >
            Ou publier maintenant
          </el-checkbox>

          <!-- Option de suppression uniquement pour les posts planifiés -->
          <el-checkbox
              v-if="canDeleteNetwork(network.name)"
              v-model="deleteOptions[network.name]"
              @change="onDeleteChange(network.name, $event)"
              class="delete-checkbox"
          >
            Supprimer la planification
          </el-checkbox>
        </div>
      </div>
      <!-- Message d'avertissement de suppression à côté du nom -->
      <div v-if="deleteOptions[network.name]" class="deletion-warning inline">
        <span> Attention, vous êtes sur le point de supprimer cette planification.</span>
      </div>
    </div>

    <template #footer>
      <el-button
          type="primary"
          @click="handleConfirm"
          :loading="isProcessing"
          :disabled="!hasValidSelection"
      >
        {{ isProcessing ? 'Publication en cours...' : 'Appliquer mes choix de publication' }}
      </el-button>
      <el-button @click="handleClose">Fermer</el-button>
    </template>
  </el-dialog>
</template>

<script>
import { getDiffuseur } from '@/helpers/functions/diffuseur'
import moment from 'moment'
import { $t } from '@/i18n'

export default {
  name: 'PublishScheduleModal',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    networks: {
      type: Array,
      required: true
    },
    post: {
      type: Object,
      required: false,
      default: () => null
    },
    universe: {
      type: String,
       required: false,
      default: ''
    },
    isBienVendu: {
      type: Boolean,
      default: false
    },
    activeNetworks: {
      type: Array,
      required: true
    },
    getCaptionByNetwork: {
      type: Function,
      required: true
    }
  },
  data () {
    return {
      selectedNetworks: {},
      scheduleOptions: {},
      scheduleDates: {},
      publishNowOptions: {},
      deleteOptions: {},
      isProcessing: false,
      initialActiveNetworks: {},
      pickerOptions: {
        firstDayOfWeek: 1,
        disabledDate (time) {
          return time.getTime() < Date.now() - 8.64e7
        }
      }
    }
  },
  computed: {
    dialogWidth () {
      if (typeof window !== 'undefined') {
        const width = window.innerWidth
        if (width < 480) return '95%'
        if (width < 768) return '90%'
        if (width < 1024) return '80%'
        if (width < 1200) return '70%'
        return '800px'
      }
      return '800px'
    },
    availableNetworks () {
      return this.networks.filter(network => {
        const networkStatus = this.socialStatusPosts[network.name]
        return network.active || (networkStatus && (networkStatus.status === 'published' || networkStatus.status === 'scheduled'))
      })
    },
    hasValidSelection () {
      return this.availableNetworks.some(network => {
        // Exclure les réseaux publiés du calcul de sélection valide
        if (this.isPublished(network.name)) {
          return false
        }

        const hasScheduledDate = this.scheduleDates[network.name] &&
            this.scheduleDates[network.name].trim() !== '' &&
            !this.publishNowOptions[network.name] &&
            !this.deleteOptions[network.name]
        const hasPublishNow = this.publishNowOptions[network.name]
        const hasDelete = this.deleteOptions[network.name]
        return hasScheduledDate || hasPublishNow || hasDelete
      })
    },
    socialStatusPosts () {
      return this.$store.getters['postManager/getSocialStatusPosts']
    },
    // Déplacer la logique de vérification des captions vides ici
    hasEmptyCaptions () {
      return this.activeNetworks.some(network => {
        const diffuseur = getDiffuseur(network.name, this.universe, this.isBienVendu)
        const caption = this.getCaptionByNetwork(diffuseur, this.$route.params.id)
        return !caption || !caption.text || caption.text.trim() === ''
      })
    },
    emptyCaptionNetworks () {
      return this.activeNetworks
        .filter(network => {
          const diffuseur = getDiffuseur(network.name, this.universe, this.isBienVendu)
          const caption = this.getCaptionByNetwork(diffuseur, this.$route.params.id)
          return !caption || !caption.text || caption.text.trim() === ''
        })
        .map(network => network.name)
    },
    missingMessage () {
      if (this.hasEmptyCaptions) {
        const networksList = this.emptyCaptionNetworks.join(', ')
        return $t('socialMedia.emptyCaptionMessage1') + networksList + this.$t('socialMedia.emptyCaptionMessage2')
      }
    }
  },
  watch: {
    visible (newVal) {
      if (newVal) {
        this.$nextTick(() => {
          this.initializeData()
        })
      }
    },
    socialStatusPosts: {
      handler (newVal) {
        if (this.visible && newVal) {
          this.$nextTick(() => {
            this.initializeData()
          })
        }
      },
      deep: true
    }
  },
  methods: {
    initializeData () {
      const selectedNets = {}
      const scheduleOpts = {}
      const scheduleDts = {}
      const publishNowOpts = {}
      const deleteOpts = {}
      const initialActive = {}

      this.availableNetworks.forEach(network => {
        const networkStatus = this.socialStatusPosts[network.name]

        initialActive[network.name] = network.active
        selectedNets[network.name] = true
        publishNowOpts[network.name] = false
        deleteOpts[network.name] = false

        if (networkStatus && networkStatus.status === 'scheduled') {
          scheduleOpts[network.name] = 'scheduled'
          if (networkStatus.date) {
            // Utiliser la date existante pour les posts déjà planifiés
            scheduleDts[network.name] = this.convertToInputFormat(networkStatus.date)
          } else {
            // Même pour les posts scheduled sans date, ne pas mettre de date par défaut
            scheduleDts[network.name] = null
          }
        } else if (networkStatus && networkStatus.status === 'published') {
          scheduleOpts[network.name] = 'now'
          publishNowOpts[network.name] = true
          scheduleDts[network.name] = null
        } else {
          // Pour les nouveaux réseaux, ne pas mettre de date par défaut
          scheduleOpts[network.name] = 'scheduled'
          scheduleDts[network.name] = null // Au lieu de this.getDefaultScheduleDate()
        }
      })

      this.selectedNetworks = selectedNets
      this.scheduleOptions = scheduleOpts
      this.scheduleDates = scheduleDts
      this.publishNowOptions = publishNowOpts
      this.deleteOptions = deleteOpts
      this.initialActiveNetworks = initialActive
    },
    canDeleteNetwork (networkName) {
      const networkStatus = this.socialStatusPosts[networkName]
      // On peut supprimer uniquement si le status est 'scheduled'
      return networkStatus && networkStatus.status === 'scheduled'
    },
    isPublished (networkName) {
      const networkStatus = this.socialStatusPosts[networkName]
      // Sécurité renforcée : vérifier que les données existent et que le statut est bien 'published'
      return networkStatus && networkStatus.status === 'published'
    },
    onPublishNowChange (networkName, checked) {
      this.$set(this.publishNowOptions, networkName, checked)

      if (checked) {
        this.$set(this.scheduleOptions, networkName, 'now')
        // Vider la date quand on choisit "publier maintenant"
        this.$set(this.scheduleDates, networkName, null)
        // Désactiver la suppression si on choisit "publier maintenant"
        this.$set(this.deleteOptions, networkName, false)
      } else {
        this.$set(this.scheduleOptions, networkName, 'scheduled')
      }
    },
    onDeleteChange (networkName, checked) {
      this.$set(this.deleteOptions, networkName, checked)

      if (checked) {
        // Si on choisit de supprimer, désactiver uniquement "publier maintenant"
        // mais garder la date dans le date picker
        this.$set(this.publishNowOptions, networkName, false)
        this.$set(this.scheduleOptions, networkName, 'delete')
      } else {
        this.$set(this.scheduleOptions, networkName, 'scheduled')
      }
    },
    // NOUVELLE MÉTHODE : Génère une date par défaut pour la planification
    getDefaultScheduleDate () {
      const defaultDate = new Date()
      defaultDate.setHours(defaultDate.getHours() + 1)
      // Retourner au format attendu par Element UI
      return moment(defaultDate).format('YYYY-MM-DD HH:mm:ss')
    },
    // MÉTHODE CORRIGÉE : Conversion qui préserve les heures locales
    convertToInputFormat (dateString) {
      if (!dateString) return null

      // Si c'est déjà au bon format, le retourner tel quel
      if (dateString.match(/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/)) {
        return dateString
      }

      // Si c'est au format DD/MM/YYYY HH:mm:ss (format d'affichage)
      if (dateString.match(/^\d{2}\/\d{2}\/\d{4} \d{2}:\d{2}:\d{2}$/)) {
        // Parser comme heure locale, pas UTC
        const momentDate = moment(dateString, 'DD/MM/YYYY HH:mm:ss')
        if (momentDate.isValid()) {
          return momentDate.format('YYYY-MM-DD HH:mm:ss')
        }
      }

      // Pour les autres formats, essayer de parser avec moment en préservant l'heure locale
      try {
        const momentDate = moment(dateString)
        if (momentDate.isValid()) {
          // Ne pas utiliser toISOString() qui convertit en UTC
          return momentDate.format('YYYY-MM-DD HH:mm:ss')
        }
      } catch (e) {
        console.warn('Impossible de convertir la date:', dateString)
      }

      return null
    },

    async handleConfirm () {
      if (!this.hasValidSelection) {
        this.$message.warning('Veuillez sélectionner au moins une action.')
        return
      }

      this.isProcessing = true

      try {
        const schedulingData = []
        const deletionsData = []

        this.availableNetworks.forEach(network => {
          const networkName = network.name
          const diffuseur = getDiffuseur(networkName, this.universe, this.isBienVendu)

          // NOUVELLE VÉRIFICATION : Empêcher toute action sur les réseaux publiés
          if (this.isPublished(networkName)) {
            return // Skip ce réseau
          }

          if (this.deleteOptions[networkName]) {
            // Ajouter à la liste des suppressions (seulement si pas publié)
            deletionsData.push({
              network: networkName,
              diffuseur: diffuseur
            })
          } else if (this.publishNowOptions[networkName]) {
            // Ajouter à la planification immédiate (seulement si pas publié)
            schedulingData.push({
              network: networkName,
              diffuseur: diffuseur,
              option: 'now',
              date: moment().format('YYYY-MM-DD HH:mm:ss')
            })
          } else if (this.scheduleDates[networkName] && this.scheduleDates[networkName].trim() !== '') {
            // Ajouter à la planification différée (seulement si pas publié)
            schedulingData.push({
              network: networkName,
              diffuseur: diffuseur,
              option: 'scheduled',
              date: this.scheduleDates[networkName]
            })
          }
        })

        // Vérification finale : s'assurer qu'aucun réseau publié n'est dans les données
        const publishedNetworksInScheduling = schedulingData.filter(item =>
          this.isPublished(item.network)
        )

        const publishedNetworksInDeletions = deletionsData.filter(item =>
          this.isPublished(item.network)
        )

        if (publishedNetworksInScheduling.length > 0 || publishedNetworksInDeletions.length > 0) {
          const problematicNetworks = [
            ...publishedNetworksInScheduling.map(item => item.network),
            ...publishedNetworksInDeletions.map(item => item.network)
          ].join(', ')

          this.$message.error(`Erreur de sécurité: Tentative de modification de réseaux publiés: ${problematicNetworks}`)
          return
        }

        // Si aucune action valide après filtrage, informer l'utilisateur
        if (schedulingData.length === 0 && deletionsData.length === 0) {
          this.$message.info('Aucune action valide à effectuer. Les réseaux publiés ne peuvent pas être modifiés.')
          return
        }

        this.$emit('confirm-publish', schedulingData, deletionsData)

        this.$emit('close')
        this.$router.push({
          name: 'social-posts',
          params: { activeName: 'Annonces' }
        })
      } catch (error) {
        console.error('Erreur lors de la planification:', error)
        this.$message.error('Erreur lors de la planification des publications.')
      } finally {
        this.isProcessing = false
      }
    },
    handleClose () {
      const networksToDeactivate = []

      Object.keys(this.initialActiveNetworks).forEach(networkName => {
        const wasInitiallyActive = this.initialActiveNetworks[networkName]
        const networkStatus = this.socialStatusPosts[networkName]
        const hasExistingPlanification = networkStatus && (networkStatus.status === 'published' || networkStatus.status === 'scheduled')
        const hasNewPlanification = this.publishNowOptions[networkName] || this.scheduleDates[networkName]

        if (wasInitiallyActive && !hasNewPlanification && !hasExistingPlanification) {
          networksToDeactivate.push(networkName)
        }
      })

      if (networksToDeactivate.length > 0) {
        this.$emit('deactivate-networks', networksToDeactivate)
      }

      this.$emit('close')
    }
  }
}
</script>

<style scoped>
.schedule-description {
  font-size: 14px;
  color: #555;
  margin-bottom: 20px;
}

.network-group {
  width: 100%;
  display: flex;
  flex-direction: column;
  margin-bottom: 24px;
  gap: 12px;
}

.network-header {
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  margin-bottom: 8px;
}

.network-title-group {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.network-controls {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}

.checkbox-group {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}

.left {
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
}

.right-options {
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: flex-start;
  margin-top: 26px;
}

.network-label {
  display: flex;
  align-items: center;
  font-weight: 600;
  font-size: 14px;
  color: #1b4f72;
  gap: 6px;
  flex-shrink: 0;
}

.info-message {
  background: linear-gradient(135deg, #e3f2fd 0%, #f0f9ff 100%);
  border-left: 4px solid #1a4a56;
  padding: 16px 20px;
  margin: 16px 0;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  font-size: 14px;
  line-height: 1.5;
  color: #1a4a56;
}

.info-message .el-icon-info {
  font-size: 16px;
  color: #00a4bd;
  margin-top: 2px;
  flex-shrink: 0;
}

.info-message p {
  margin: 0;
  color: #1a4a56;
  font-weight: 500;
}

.icon {
  width: 18px;
  height: 18px;
}

.date-picker {
  min-width: 200px;
  max-width: 220px;
  flex-shrink: 0;
}

.publish-now-checkbox {
  font-size: 13px;
  white-space: nowrap;
}

.delete-checkbox {
  font-size: 13px;
  white-space: nowrap;
  color: #e74c3c;
}

.deletion-warning {
  font-size: 12px;
  white-space: nowrap;
  color: #e74c3c;
  background-color: #fdf2f2;
  padding: 4px 8px;
  border-left: 2px solid #e74c3c;
  flex-shrink: 0;
}

.deletion-warning.inline {
  font-size: 13px;
  padding: 2px 6px;
}

.published-message {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background-color: #f0f9ff;
  border: 1px solid #bee5eb;
  border-radius: 4px;
  color: #0c5460;
  font-size: 13px;
  font-weight: 500;
}

.published-message .el-icon-info {
  color: #17a2b8;
  font-size: 14px;
}

/* Responsive design */
@media (max-width: 1024px) {
  .network-controls {
    gap: 12px;
  }

  .checkbox-group {
    gap: 12px;
  }

  .date-picker {
    min-width: 180px;
    max-width: 200px;
  }
}

@media (max-width: 768px) {
  .network-controls {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }

  .checkbox-group {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .date-picker {
    width: 100%;
    max-width: none;
    min-width: auto;
  }

  .network-title-group {
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
  }

  .deletion-warning.inline {
    font-size: 12px;
    padding: 4px 8px;
  }
}

@media (max-width: 480px) {
  .checkbox-group {
    gap: 6px;
  }

  .publish-now-checkbox,
  .delete-checkbox {
    font-size: 12px;
  }

  .deletion-warning,
  .deletion-warning.inline {
    font-size: 11px;
    padding: 3px 6px;
  }

  .network-label {
    font-size: 13px;
  }
}

/* Styles pour les checkboxes désactivées */
.el-checkbox.is-disabled .el-checkbox__label {
  color: #c0c4cc;
}
</style>
